import type { TrackingEvent } from '@marketplace-web/shared/event-tracker-data'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import { CheckoutOrderTypeMap, ExtraServiceOrderType } from '../constants'

type BuyerViewEscrowCheckoutEventArgs = {
  transactionId: number | null
  isExternalOpen?: boolean
  screen: string
}

type BuyerViewExtraServiceCheckoutEventArgs = {
  orderId: number
  orderType: ExtraServiceOrderType
  screen: string
}

type BuyerViewEscrowCheckoutEventExtra = {
  transaction_id: number | string
  entry_from_link: boolean | null
  checkout_type: 'escrow'
  screen: string
}

type BuyerViewExtraServiceCheckoutEventExtra = {
  order_id: number
  checkout_type: string
  screen: string
}

type BuyerViewCheckoutEventArgs =
  | BuyerViewEscrowCheckoutEventArgs
  | BuyerViewExtraServiceCheckoutEventArgs

export const buyerViewCheckoutEvent = (args: BuyerViewCheckoutEventArgs): TrackingEvent => {
  const event = 'buyer.view_checkout'

  if ('transactionId' in args) {
    const extra: BuyerViewEscrowCheckoutEventExtra = {
      screen: args.screen,
      checkout_type: 'escrow',
      transaction_id: args.transactionId ?? '',
      entry_from_link: args.isExternalOpen === undefined ? null : !!args.isExternalOpen,
    }

    return { event, extra }
  }

  const extra: BuyerViewExtraServiceCheckoutEventExtra = {
    screen: args.screen,
    order_id: args.orderId,
    checkout_type: CheckoutOrderTypeMap[args.orderType],
  }

  return { event, extra }
}

type ViewSingleCheckoutScreenEventArgs = {
  screen?: string
  orderId?: string
  orderType?: string
  checkoutId?: string | null
}

type ViewSingleCheckoutScreenEventExtra = {
  screen?: string
  order_id?: string
  order_type?: string
  checkout_id?: string | null
}

export const viewSingleCheckoutEvent = (args: ViewSingleCheckoutScreenEventArgs): TrackingEvent => {
  const { checkoutId, screen, orderId, orderType } = args

  const extra: ViewSingleCheckoutScreenEventExtra = {
    checkout_id: checkoutId,
    screen: screen || Screen.Checkout,
  }

  if (orderId) extra.order_id = orderId
  if (orderType) extra.order_type = orderType

  return { event: 'checkout.screen', extra }
}

type ViewAdditionalServicesModalEventArgs = {
  checkoutId: string
  screen: Screen
  target: string
}

export const viewAdditionalServicesModalEvent = (
  args: ViewAdditionalServicesModalEventArgs,
): TrackingEvent => {
  const { checkoutId, screen, target } = args

  return {
    event: 'checkout.click',
    extra: {
      checkout_id: checkoutId,
      screen,
      target,
    },
  }
}
type ClickBuyerProtectionFeeInfoEventArgs = {
  checkoutId: string
  target: ClickableElement
  screen: Screen
}

export const clickBuyerProtectionFeeInfoEvent = (
  args: ClickBuyerProtectionFeeInfoEventArgs,
): TrackingEvent => {
  const { checkoutId, screen, target } = args

  return {
    event: 'checkout.click',
    extra: {
      checkout_id: checkoutId,
      screen,
      target,
    },
  }
}

type ViewCurrencyConversionPricingDetailsEventArgs = {
  checkoutId: string
  screen: Screen
}

export const viewCurrencyConversionPricingDetailsEvent = (
  args: ViewCurrencyConversionPricingDetailsEventArgs,
): TrackingEvent => {
  const { checkoutId, screen } = args

  return {
    event: 'checkout.screen',
    extra: {
      checkout_id: checkoutId,
      screen,
    },
  }
}

type ViewSalesTaxEventArgs = {
  checkoutId: string
  screen: Screen
}

export const viewSalesTaxEvent = (args: ViewSalesTaxEventArgs): TrackingEvent => {
  const { checkoutId, screen } = args

  return {
    event: 'checkout.screen',
    extra: {
      checkout_id: checkoutId,
      screen,
    },
  }
}
type ViewEscrowFeeEducationEventArgs = {
  checkoutId: string
  screen: Screen
}

export const viewEscrowFeeEducationEvent = (
  args: ViewEscrowFeeEducationEventArgs,
): TrackingEvent => {
  const { checkoutId, screen } = args

  return {
    event: 'checkout.screen',
    extra: {
      checkout_id: checkoutId,
      screen,
    },
  }
}

type ClickEscrowFeeCalculationEventArgs = {
  checkoutId: string
  target: ClickableElement
  screen: Screen
}

export const clickEscrowFeeCalculationEvent = (
  args: ClickEscrowFeeCalculationEventArgs,
): TrackingEvent => {
  const { checkoutId, screen, target } = args

  return {
    event: 'checkout.click',
    extra: {
      checkout_id: checkoutId,
      screen,
      target,
    },
  }
}

type CloseBuyerProtectionModalEventArgs = {
  checkoutId: string
  target: ClickableElement
  screen: Screen
}

export const closeBuyerProtectionModalEvent = (
  args: CloseBuyerProtectionModalEventArgs,
): TrackingEvent => {
  const { checkoutId, screen, target } = args

  return {
    event: 'checkout.click',
    extra: {
      checkout_id: checkoutId,
      screen,
      target,
    },
  }
}

type ClickSingleCheckoutEventArgs = {
  checkoutId?: string
  screen?: string
  target?: string
  valid?: boolean
  info?: {
    card_details_stored?: boolean
    payment_method?: string | null
    pickup_type?: string
    item_id?: string
  }
}

type ClickSingleCheckoutEventExtra = {
  checkout_id?: string
  screen?: string
  target?: string
  valid?: boolean
  info?: string
}

export const clickSingleCheckoutEvent = (args: ClickSingleCheckoutEventArgs): TrackingEvent => {
  const { target, checkoutId, screen, valid, info } = args

  const extra: ClickSingleCheckoutEventExtra = {
    target,
    checkout_id: checkoutId,
    screen: screen || Screen.Checkout,
  }

  if (valid) extra.valid = valid
  if (info) extra.info = JSON.stringify(info)

  return {
    event: 'checkout.click',
    extra,
  }
}

type ClickSelectVerificationServiceEventArgs = {
  checkoutId: string
  screen: string
  target: string
  enabled: boolean
}

type ClickSelectVerificationServiceEventExtra = {
  checkout_id: string
  screen: string
  target: string
  info: {
    check_authenticity: boolean
  }
}

export const clickSelectVerificationServiceEvent = (
  args: ClickSelectVerificationServiceEventArgs,
): TrackingEvent => {
  const { target, checkoutId, screen, enabled } = args

  const extra: ClickSelectVerificationServiceEventExtra = {
    checkout_id: checkoutId,
    screen,
    target,
    info: {
      check_authenticity: enabled,
    },
  }

  return {
    event: 'checkout.click',
    extra,
  }
}

type SingleCheckoutInteractionEventArgs = {
  checkoutId: string
  actionDetails: number
}

export const singleCheckoutInteractionEvent = (
  args: SingleCheckoutInteractionEventArgs,
): TrackingEvent => {
  const { actionDetails, checkoutId } = args

  return {
    event: 'checkout.interaction',
    extra: {
      screen: Screen.Checkout,
      action: 'screen_load_duration_in_ms',
      checkout_id: checkoutId,
      duration: actionDetails,
    },
  }
}

type SingleCheckoutTimeOnTaskEventArgs = {
  checkoutId: string
  screen?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type SingleCheckoutTimeOnTaskEventExtra = {
  checkout_id: string
  screen: string
  action: string
  duration: number
  action_details: string
}

export const singleCheckoutTimeOnTaskEvent = (args: SingleCheckoutTimeOnTaskEventArgs) => {
  const { checkoutId, screen, duration, utc_time_from, utc_time_to } = args

  const extra: SingleCheckoutTimeOnTaskEventExtra = {
    checkout_id: checkoutId,
    screen: screen || '',
    action: 'time_on_task_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'checkout.interaction',
    extra,
  }
}

type ViewAddMoreButtonEventArgs = {
  checkoutId: string
  screen: Screen
  capabilityType: string
  capabilityDetails: boolean
}

export const viewAddMoreButtonEvent = (args: ViewAddMoreButtonEventArgs): TrackingEvent => {
  const { checkoutId, screen, capabilityType, capabilityDetails } = args

  return {
    event: 'checkout.buyer_has_capability',
    extra: {
      checkout_id: checkoutId,
      screen,
      capability_details: capabilityDetails,
      capability_type: capabilityType,
    },
  }
}
